import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  DeleteAgentValidationSchema,
  TDeleteAgentValidation,
  useDeleteAgentMutation,
} from '../../../api/delete-agent.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_AGENT_QUERY_KEY } from '../../../api/get-list-agent-api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';
import { GET_QUOTA_AGENT_QUERY_KEY } from '../../../api/get-quota-agent.api';

export type TDeleteAgentModalType = 'DeleteAgentModal';
export interface IDeleteAgentModalProps {
  agentId: string;
}

const DeleteAgentModal = memo(({ agentId }: IDeleteAgentModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteAgentModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('DeleteAgentModal');
  }, [userModalRootActions]);

  const form = useForm<TDeleteAgentValidation>({
    resolver: zodResolver(DeleteAgentValidationSchema),
    mode: 'onChange',
    defaultValues: {
      agentId: agentId || '',
    },
  });

  useEffect(() => {
    if (agentId) {
      form.reset({
        agentId: agentId || '',
      });
    }
  }, [agentId, form]);

  const mutationDeleteAgent = useDeleteAgentMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete agent, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_QUOTA_AGENT_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY] }),
        ]);
        userModalRootActions.closeModal('DeleteAgentModal');
        showToast({
          message: 'Agent deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteAgent = useCallback(
    (data: TDeleteAgentValidation) => {
      mutationDeleteAgent.mutate({
        agentId: data?.agentId || '',
      });
    },
    [mutationDeleteAgent]
  );

  const handleClose = useCallback(() => {
    userModalRootActions.closeModal('DeleteAgentModal');
  }, [userModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteAgentModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Agent</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteAgent)(e);
          }}
          id="delete-agent-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this agent?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-agent-form" type="submit" disabled={mutationDeleteAgent.isPending}>
          {mutationDeleteAgent.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteAgentModal.displayName = 'DeleteAgentModal';

export default DeleteAgentModal;
